// Articles API Client
// Uses Neon PostgreSQL backend via Vercel Functions

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '/api';

// Helper function to make API requests
async function fetchFromAPI(endpoint, params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_ENDPOINT}${endpoint}${queryString ? `?${queryString}` : ''}`;

  try {
    const response = await fetch(url);

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.warn('API returned non-JSON response');
      if (endpoint.includes('/articles')) {
        return params.action === 'single' ? null : [];
      }
      throw new Error('Invalid API response');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `API request failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API fetch error:', error);
    if (endpoint.includes('/articles')) {
      return params.action === 'single' ? null : [];
    }
    throw error;
  }
}

// Fetch all articles
export async function fetchArticles(limit = 20, filterPublished = true) {
  try {
    const articles = await fetchFromAPI('/articles', {
      limit: limit.toString(),
      filterPublished: filterPublished.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error fetching articles:', error);
    return [];
  }
}

// Fetch single article by slug
export async function fetchArticleBySlug(slug) {
  if (!slug) return null;

  try {
    const article = await fetchFromAPI('/articles', {
      action: 'single',
      slug
    });
    return article;
  } catch (error) {
    console.error('Error fetching article by slug:', error);
    return null;
  }
}

// Fetch articles by category
export async function fetchArticlesByCategory(category, limit = 10) {
  if (!category) return [];

  try {
    const articles = await fetchFromAPI('/articles', {
      action: 'category',
      category,
      limit: limit.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error fetching articles by category:', error);
    return [];
  }
}

// Fetch featured articles
export async function fetchFeaturedArticles(limit = 3) {
  try {
    const articles = await fetchFromAPI('/articles', {
      action: 'featured',
      limit: limit.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error fetching featured articles:', error);
    return [];
  }
}

// Search articles
export async function searchArticles(query, limit = 10) {
  if (!query || query.length < 2) return [];

  try {
    const articles = await fetchFromAPI('/articles', {
      action: 'search',
      query,
      limit: limit.toString()
    });
    return Array.isArray(articles) ? articles : [];
  } catch (error) {
    console.error('Error searching articles:', error);
    return [];
  }
}

// Check if API is configured
export async function isAPIConfigured() {
  try {
    const response = await fetch(`${API_ENDPOINT}/health`);
    if (response.ok) {
      const data = await response.json();
      return data.status === 'healthy';
    }
    return false;
  } catch {
    return false;
  }
}
